import { useIdentity } from '@/auth/useIdentity';
import { resolveStageRoute } from './stageRoutes';
import type { ActiveWorkflowDto } from '@/types';

export interface ActiveWorkflowState {
  /** The active workflow, or null when the user is free OR the stage is unknown. */
  activeWorkflow: ActiveWorkflowDto | null;
  /** Route for the active workflow's stage, or null. */
  stageRoute: string | null;
  /** /me is loading for the first time. */
  isLoading: boolean;
  /** /me is being refetched (e.g. after a gate-release invalidation). */
  isFetching: boolean;
  /** /me has not settled yet — pre-auth skip window or first load (see useIdentity). */
  isUninitialized: boolean;
}

/**
 * Single source of truth for the single-active-translation gate. Reads the
 * activeWorkflow pointer from /me and resolves it to a route. An unknown/future
 * stage is logged and treated as "no workflow" so a new backend stage can't break
 * the app.
 */
export function useActiveWorkflow(): ActiveWorkflowState {
  const { user, isLoading, isFetching, isUninitialized } = useIdentity();
  const raw = user?.activeWorkflow ?? null;

  if (!raw) {
    return { activeWorkflow: null, stageRoute: null, isLoading, isFetching, isUninitialized };
  }

  const stageRoute = resolveStageRoute(raw.stage);
  if (!stageRoute) {
    console.warn(`[activeWorkflow] Unknown stage "${raw.stage}"; falling back to normal UI.`);
    return { activeWorkflow: null, stageRoute: null, isLoading, isFetching, isUninitialized };
  }

  return { activeWorkflow: raw, stageRoute, isLoading, isFetching, isUninitialized };
}
